import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAGE_SIZES, pageItems } from "@/lib/documents/document-list";

type ListPaginationProps = {
  page: number;
  totalPages: number;
  totalItems: number;
  perPage: number;
  onPage: (page: number) => void;
  onSizeChange: (size: number) => void;
};

/**
 * "51–100 of 432", page numbers (just previous / next on a phone) and the page size. Hidden while
 * everything fits on the smallest page.
 */
export function ListPagination({
  page,
  totalPages,
  totalItems,
  perPage,
  onPage,
  onSizeChange,
}: ListPaginationProps) {
  const { t } = useTranslation();
  if (totalItems <= PAGE_SIZES[0] && totalPages <= 1) return null;

  const first = totalItems ? (page - 1) * perPage + 1 : 0;
  const last = Math.min(page * perPage, totalItems);
  const step = (target: number, disabled: boolean, label: string, icon: React.ReactNode) => (
    <Button
      variant="outline"
      size="icon"
      className="size-8 bg-card"
      disabled={disabled}
      aria-label={label}
      onClick={() => onPage(target)}
    >
      {icon}
    </Button>
  );

  return (
    <nav
      aria-label={t("pagination.label")}
      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3"
      data-testid="pagination"
    >
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="num" data-testid="pagination-range">
          {t("pagination.range", { first, last, total: totalItems })}
        </span>
        <Select value={String(perPage)} onValueChange={(value) => onSizeChange(Number(value))}>
          <SelectTrigger
            className="h-8 w-auto gap-2"
            aria-label={t("pagination.size")}
            data-testid="page-size"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {t("pagination.perPage", { size })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {step(page - 1, page <= 1, t("pagination.previous"), <ChevronLeft />)}
          <span className="px-2 text-sm text-muted-foreground num sm:hidden">
            {t("pagination.page", { page, total: totalPages })}
          </span>
          <ul className="hidden items-center gap-1 sm:flex">
            {pageItems(page, totalPages).map((item, index) =>
              item === "gap" ? (
                <li key={`gap-${index}`} className="w-6 text-center text-muted-foreground">
                  …
                </li>
              ) : (
                <li key={item}>
                  <Button
                    variant={item === page ? "default" : "ghost"}
                    size="sm"
                    className="h-8 min-w-8 px-2 num"
                    aria-current={item === page ? "page" : undefined}
                    aria-label={t("pagination.goTo", { page: item })}
                    onClick={() => onPage(item)}
                  >
                    {item}
                  </Button>
                </li>
              ),
            )}
          </ul>
          {step(page + 1, page >= totalPages, t("pagination.next"), <ChevronRight />)}
        </div>
      )}
    </nav>
  );
}
